import { useState, useEffect } from 'react'
import { Box, Flex, VStack } from '@chakra-ui/react'
import { Modal, Text, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { SHIPMENT_STATE_MAP } from '../../lib/shipmentStates'
import { shipmentsService } from '../../api/services/shipments.service'

interface Props {
  shipmentId: string | null
  batchCode?: string
  currentState?: number
  onClose: () => void
  onChanged: () => void
}

const STATE_OPTIONS = Object.entries(SHIPMENT_STATE_MAP)
  .map(([id, def]) => ({ id: Number(id), ...def }))
  .sort((a, b) => a.id - b.id)

export function ForceShipmentStateModal({
  shipmentId,
  batchCode,
  currentState,
  onClose,
  onChanged,
}: Props) {
  const colors = themeColors
  const [selected, setSelected] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setSelected(currentState ?? null)
  }, [shipmentId, currentState])

  const handleSave = async () => {
    if (!shipmentId || selected == null) return
    if (selected === currentState) {
      onClose()
      return
    }

    setSaving(true)
    try {
      await shipmentsService.forceSetState(shipmentId, selected)
      toaster.success({ description: 'Estado actualizado.' })
      onChanged()
      onClose()
    } catch {
      // handled by apiClient
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={shipmentId != null}
      onClose={saving ? () => {} : onClose}
      title="Cambiar estado del traslado"
      subtitle={batchCode ? `Lote ${batchCode}` : undefined}
      size="md"
    >
      <VStack gap={4} alignItems="stretch">
        <Box
          p={3}
          borderRadius="lg"
          bg={`${brand.gold}15`}
          border="1px solid"
          borderColor={`${brand.gold}40`}
        >
          <Text fontSize="xs" color={colors.textSecondary}>
            Herramienta de pruebas. Cambia el estado del traslado sin validar la transición. Se
            registra un evento con la anotación <b>[DEV OVERRIDE]</b>.
          </Text>
        </Box>

        <Box>
          <Text fontSize="sm" fontWeight="600" color={colors.text} mb={2}>
            Nuevo estado
          </Text>
          <VStack gap={2} alignItems="stretch">
            {STATE_OPTIONS.map((opt) => {
              const isSelected = selected === opt.id
              const isCurrent = currentState === opt.id
              return (
                <Box
                  key={opt.id}
                  as="button"
                  type="button"
                  textAlign="left"
                  px={3}
                  py={2}
                  borderRadius="lg"
                  bg={isSelected ? `${opt.color}20` : colors.bgCard}
                  border="1px solid"
                  borderColor={isSelected ? opt.color : colors.border}
                  cursor="pointer"
                  _hover={{ borderColor: opt.color }}
                  onClick={() => setSelected(opt.id)}
                >
                  <Flex alignItems="center" justifyContent="space-between">
                    <Flex alignItems="center" gap={3}>
                      <Box w="10px" h="10px" borderRadius="full" bg={opt.color} />
                      <Text fontSize="sm" fontWeight="600" color={colors.text}>
                        {opt.label}
                      </Text>
                      <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">
                        ({opt.id})
                      </Text>
                    </Flex>
                    {isCurrent && (
                      <Text fontSize="xs" color={colors.textMuted}>
                        Estado actual
                      </Text>
                    )}
                  </Flex>
                </Box>
              )
            })}
          </VStack>
        </Box>

        <Flex gap={2} justifyContent="flex-end">
          <Box
            as="button"
            type="button"
            px={4}
            py={2}
            borderRadius="lg"
            border="1px solid"
            borderColor={colors.border}
            color={colors.text}
            fontWeight="600"
            fontSize="sm"
            cursor={saving ? 'not-allowed' : 'pointer'}
            _hover={{ bg: colors.bgHover }}
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </Box>
          <Box
            as="button"
            type="button"
            px={4}
            py={2}
            borderRadius="lg"
            bg={brand.green}
            color="white"
            fontWeight="600"
            fontSize="sm"
            cursor={saving || selected == null ? 'not-allowed' : 'pointer'}
            opacity={saving || selected == null ? 0.6 : 1}
            _hover={{ opacity: 0.9 }}
            onClick={handleSave}
            disabled={saving || selected == null}
          >
            {saving ? 'Guardando…' : 'Aplicar'}
          </Box>
        </Flex>
      </VStack>
    </Modal>
  )
}
